// Login Modal - Phase 1.13 component-based composition.
//
// Wraps `@nostr-gives/components` primitives (ModalShell + SegmentedTab +
// ActionButton) around the Phase 1.5 real-signer auth flow:
//
// Bunker tab:
//   - Generates a `nostrconnect://` URI on mount (we display, signer scans).
//   - Renders QR via `renderBunkerQr` + tap-to-copy URI field.
//   - "Connect" awaits the signer dial-back; signs the worker challenge;
//     POSTs `/api/auth/nip46/verify`; stores the session.
//
// Extension tab:
//   - Detects window.nostr.
//   - "Sign in" calls nip07.signIn(), then signs the same challenge.
//
// Nsec tab (Phase 1.5 follow-up - parity with surf/gold/press/TNR):
//   - Direct nsec1 paste. Validates locally via `nostr-tools` nip19.
//   - Carries a security note: nsec is encrypted at rest (SecretBox) in
//     the browser, no remote signer involved. Never sent to the Worker.
//   - On success, derives pubkey + stores `gives.nsec` for future
//     finalizeEvent-based signing inside `publishToNostr()`.
//
// Secondary "Play without signing in" writes an anon session, same shape
// as the Phase 1.4 stub.
//
// Mockup: Vault/Design/Mockups/_Notes - Login Modal.md
// Threat model: Vault/Research/Nostr Safety - Follow List Hygiene.md

import {
  iconSvg,
  mountActionButton,
  mountModalShell,
  mountSegmentedTab,
  type ButtonHandle,
  type ModalHandle,
  type TabHandle,
} from '@nostr-gives/components';
import {
  nip07,
  nip46,
  nsec as nsecMod,
  renderBunkerQr,
  NoExtensionError,
  type AuthResult,
  type NostrSigner,
  type SignedNostrEvent,
} from '@nostr-gives/nostr-auth';
import { BUNKER_RELAYS } from '@nostr-gives/shared';
import { saveAuth, saveNsec } from '../lib/session';
import { apiJson } from '../lib/api';
import { fetchProfile } from '../lib/profile';

type Tab = 'bunker' | 'extension' | 'nsec';

export interface LoginModalResult {
  pubkey: string;
}

interface ChallengeStartResponse {
  challenge: string;
  relay: string;
  ttlSec: number;
}

interface VerifyResponse {
  sessionToken: string;
  pubkey: string;
  exp: number;
}

const TAB_ITEMS = [
  { id: 'bunker', label: 'Bunker' },
  { id: 'extension', label: 'Extension' },
  { id: 'nsec', label: 'Nsec' },
] as const;

function isTab(value: string): value is Tab {
  return value === 'bunker' || value === 'extension' || value === 'nsec';
}

/**
 * True iff we're running inside Safari on an iOS device (iPhone, iPad,
 * iPod). Chrome-for-iOS (`CriOS`) and Firefox-for-iOS (`FxiOS`) are
 * EXCLUDED because they use the WebKit shell but DO NOT suspend
 * WebSocket connections the same way Mobile Safari does on backgrounding.
 *
 * Used only to paint a mobile-specific nudge under the QR code so the
 * user knows it is safe to switch to clave and come back - the actual
 * dial-back listener runs on the Worker now (worker-proxy long-poll,
 * 2026-06-11) so we no longer regenerate the URI on visibilitychange.
 * See Vault/Operations/Clave Compat.md.
 */
function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOS = /iPad|iPhone|iPod/.test(ua);
  if (!iOS) return false;
  const safari = /Safari/.test(ua);
  const otherShell = /CriOS|FxiOS|EdgiOS|OPiOS|mercury/i.test(ua);
  return safari && !otherShell;
}

export function openLoginModal(onClose: (result: LoginModalResult | null) => void): void {
  let activeTab: Tab = 'bunker';
  let activeBunkerConnect: nip46.NostrConnectUri | null = null;
  let activePairAbort: AbortController | null = null;
  let bunkerSignerForCleanup: NostrSigner | null = null;
  let modal: ModalHandle | null = null;
  let connectButton: ButtonHandle | null = null;
  let signInButton: ButtonHandle | null = null;
  let nsecSignInButton: ButtonHandle | null = null;
  let anonButtonHandle: ButtonHandle | null = null;
  let tabHandle: TabHandle | null = null;

  const content = document.createElement('div');
  content.className = 'login-modal-content';

  const tabSlot = document.createElement('div');
  tabSlot.className = 'login-modal-tab-slot';
  content.appendChild(tabSlot);

  const tabBody = document.createElement('div');
  tabBody.className = 'login-modal-tab-body';
  content.appendChild(tabBody);

  const footerCopy = document.createElement('p');
  footerCopy.className = 'login-modal-footer';
  footerCopy.textContent = 'We never see your nsec. We only ask to sign kind 1 and kind 30078.';
  content.appendChild(footerCopy);

  const anonSlot = document.createElement('div');
  anonSlot.className = 'login-modal-anon-slot';
  content.appendChild(anonSlot);

  function close(result: LoginModalResult | null): void {
    // Cancel any in-flight bunker dial-back before tearing down DOM.
    // Without this the SimplePool inside `connectBunker()` plus the
    // long-poll fetch keep resources open until the 180s timeout fires.
    activePairAbort?.abort();
    activePairAbort = null;
    if (bunkerSignerForCleanup?.disconnect) {
      void bunkerSignerForCleanup.disconnect();
    }
    connectButton?.destroy();
    signInButton?.destroy();
    nsecSignInButton?.destroy();
    anonButtonHandle?.destroy();
    tabHandle?.destroy();
    modal?.destroy();
    onClose(result);
  }

  async function completeAuth(
    pubkey: string,
    signerType: 'nip46' | 'nip07',
    signer: NostrSigner,
  ): Promise<void> {
    const startRes = await apiJson<ChallengeStartResponse>('/api/auth/nip46/start', {
      method: 'POST',
      json: {},
    });
    if (startRes.status !== 200 || !startRes.data) {
      throw new Error(`auth/nip46/start failed: ${startRes.status}`);
    }
    const { challenge, relay } = startRes.data;

    const signed: SignedNostrEvent = await nip46.signAuthChallenge(signer, {
      challenge,
      relay,
    });

    const verifyRes = await apiJson<VerifyResponse>('/api/auth/nip46/verify', {
      method: 'POST',
      json: signed,
    });
    if (verifyRes.status !== 200 || !verifyRes.data) {
      throw new Error(`auth/nip46/verify failed: ${verifyRes.status}`);
    }
    const { sessionToken, pubkey: verifiedPubkey } = verifyRes.data;
    if (verifiedPubkey !== pubkey) {
      throw new Error('Worker returned a different pubkey than we signed with');
    }

    saveAuth({
      pubkey,
      signerType,
      sessionToken,
      authedAt: Date.now(),
    });
    // Fire-and-forget kind 0 fetch so the header PFP can paint as soon
    // as the modal closes instead of waiting on the next renderAuthControl
    // to discover the empty cache and re-fetch. We deliberately do NOT
    // await: a slow / unavailable relay should never block the sign-in
    // confirmation. See packages/frontend/src/lib/profile.ts.
    void fetchProfile(pubkey);
    close({ pubkey });
  }

  function playAnon(): void {
    const pubkey = 'anon_' + crypto.randomUUID();
    saveAuth({ pubkey, signerType: 'anon', authedAt: Date.now() });
    close({ pubkey });
  }

  // iOS-Safari users default to the bunker:// paste flow. They can
  // opt back into the QR/nostrconnect flow with the "Use QR / desktop
  // flow" toggle, which is useful for iPad-with-nearby-phone pairing.
  // Every other browser sees the QR flow only - it works fine on
  // desktop and Android.
  let bunkerForceQrOnIos = false;

  function renderBunkerTab(): void {
    tabBody.replaceChildren();
    connectButton?.destroy();
    connectButton = null;

    const useBunkerUriPaste = isIosSafari() && !bunkerForceQrOnIos;
    if (useBunkerUriPaste) {
      renderBunkerTabIosPaste();
    } else {
      renderBunkerTabQr();
    }
  }

  /**
   * iOS Safari: render the bunker:// URI paste flow.
   *
   * Per Vault/Architecture/auth-nip46/iOS Safari Research.md (Path F),
   * this is the only flow that survives Safari's WebSocket suspension
   * on backgrounding. The user opens clave FIRST, generates a bunker
   * URI, copies it, opens (or returns to) Safari, and pastes. Safari
   * stays foregrounded throughout the pairing handshake.
   */
  function renderBunkerTabIosPaste(): void {
    const headline = document.createElement('p');
    headline.className = 'modal-body-text';
    headline.style.fontWeight = '600';
    headline.textContent = 'On iPhone, paste a bunker URI:';
    tabBody.appendChild(headline);

    const steps = document.createElement('ol');
    steps.className = 'modal-body-text';
    steps.style.paddingLeft = '20px';
    steps.style.margin = '8px 0 12px';
    const step1 = document.createElement('li');
    step1.textContent = 'Open clave (or any bunker app)';
    const step2 = document.createElement('li');
    step2.textContent = 'Generate a bunker:// URI for nostr.gives';
    const step3 = document.createElement('li');
    step3.textContent = "Paste it below - we'll connect without leaving Safari.";
    steps.append(step1, step2, step3);
    tabBody.appendChild(steps);

    // Paste input. We use a textarea (not a single-line input) because
    // bunker:// URIs are long and we want the user to see what they
    // pasted before tapping Connect. iOS autocorrect / autocapitalise
    // would mangle the secret, so they're disabled.
    const inputRow = document.createElement('div');
    inputRow.className = 'bunker-paste-row';
    inputRow.style.display = 'flex';
    inputRow.style.flexDirection = 'column';
    inputRow.style.gap = '8px';
    const input = document.createElement('textarea');
    input.className = 'nsec-input mono';
    input.rows = 3;
    input.placeholder = 'bunker://...';
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('autocorrect', 'off');
    input.setAttribute('autocapitalize', 'off');
    input.setAttribute('spellcheck', 'false');
    input.setAttribute('aria-label', 'Bunker URI');
    inputRow.appendChild(input);
    tabBody.appendChild(inputRow);

    const status = document.createElement('p');
    status.className = 'modal-status';
    tabBody.appendChild(status);

    const connectBtnSlot = document.createElement('div');
    connectBtnSlot.className = 'login-modal-cta';
    tabBody.appendChild(connectBtnSlot);

    function normalizeUri(raw: string): string {
      // Trim whitespace + strip surrounding quotes some share sheets
      // wrap the URI in. We don't lowercase - the pubkey is hex (case-
      // insensitive) but the secret can be any printable.
      return raw.trim().replace(/^["']+|["']+$/g, '');
    }

    function looksLikeBunkerUri(raw: string): boolean {
      return normalizeUri(raw).startsWith('bunker://');
    }

    function updateButtonState(): void {
      const ok = looksLikeBunkerUri(input.value);
      connectButton?.update({
        variant: 'primary',
        label: 'Connect',
        disabled: !ok,
        onClick: onConnect,
      });
    }

    let busy = false;
    function onConnect(): void {
      if (busy) return;
      const raw = normalizeUri(input.value);
      if (!raw.startsWith('bunker://')) {
        status.textContent = 'Paste a bunker:// URI to continue.';
        status.dataset.kind = 'error';
        return;
      }
      busy = true;
      delete status.dataset.kind;
      status.textContent = 'Parsing URI...';
      connectButton?.update({
        variant: 'primary',
        label: 'Connecting...',
        disabled: true,
        onClick: (): void => {},
      });

      void (async (): Promise<void> => {
        const pointer = await nip46.parseBunkerUri(raw);
        if (!pointer) {
          busy = false;
          // We can't tell the user precisely WHY a parse failed without
          // re-implementing the nostr-tools regex; the most common cases
          // are (a) garbage input, (b) zero relays in the URI. The
          // error copy covers both.
          status.textContent =
            "That doesn't look like a valid bunker URI. Make sure it starts with bunker:// and includes at least one relay.";
          status.dataset.kind = 'error';
          updateButtonState();
          return;
        }
        // Defence in depth: parseBunkerUri already rejects empty
        // relay arrays, but call it out explicitly so a regression in
        // upstream nostr-tools doesn't silently hang the user on a
        // 3-minute socket timeout.
        if (pointer.relays.length === 0) {
          busy = false;
          status.textContent =
            'The bunker URI is missing a relay. Ask your signer to include `relay=` in the URI.';
          status.dataset.kind = 'error';
          updateButtonState();
          return;
        }

        const abort = new AbortController();
        activePairAbort?.abort();
        activePairAbort = abort;
        status.textContent = 'Connecting to your bunker...';
        delete status.dataset.kind;

        try {
          const result = await nip46.connectBunker({
            bunker: pointer,
            signal: abort.signal,
          });
          if (abort.signal.aborted) {
            void result.signer.disconnect?.();
            return;
          }
          bunkerSignerForCleanup = result.signer;
          await completeAuth(result.pubkey, 'nip46', result.signer);
        } catch (err: unknown) {
          if (abort.signal.aborted) return;
          busy = false;
          status.textContent = `Bunker error: ${err instanceof Error ? err.message : 'unknown'}`;
          status.dataset.kind = 'error';
          updateButtonState();
        }
      })();
    }

    connectButton = mountActionButton(connectBtnSlot, {
      variant: 'primary',
      label: 'Connect',
      disabled: true,
      onClick: onConnect,
    });

    input.addEventListener('input', updateButtonState);
    input.addEventListener('paste', () => {
      // Run after the paste lands in the input value.
      setTimeout(updateButtonState, 0);
    });
    updateButtonState();

    // Tertiary toggle: let iPad users (or anyone who prefers it) fall
    // back to the QR/nostrconnect flow. Cheap to expose and avoids
    // confusing users with a desktop nearby.
    const toggle = document.createElement('a');
    toggle.className = 'modal-link';
    toggle.href = '#';
    toggle.textContent = 'Use QR / desktop flow instead';
    toggle.addEventListener('click', (e) => {
      e.preventDefault();
      bunkerForceQrOnIos = true;
      renderBunkerTab();
    });
    tabBody.appendChild(toggle);

    const link = document.createElement('a');
    link.className = 'modal-link';
    link.href = '#/help/auth';
    link.textContent = 'What is a bunker?';
    tabBody.appendChild(link);

    setTimeout(() => input.focus(), 0);
  }

  /**
   * Desktop / non-iOS-Safari: render the nostrconnect:// QR flow.
   *
   * Unchanged from the pre-d2134e4 implementation. The signer (clave,
   * nsec.app, Amber, etc.) scans the QR, dials back over the relay,
   * and the BunkerSigner inside nip46.connectBunker() resolves.
   */
  function renderBunkerTabQr(): void {
    const card = document.createElement('div');
    card.className = 'qr-card';
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 200;
    card.appendChild(canvas);
    tabBody.appendChild(card);

    const uriRow = document.createElement('div');
    uriRow.className = 'uri-field';
    const uriText = document.createElement('code');
    uriText.className = 'uri-text mono';
    const copyBtn = document.createElement('button');
    copyBtn.type = 'button';
    copyBtn.className = 'icon-btn';
    copyBtn.setAttribute('aria-label', 'Copy bunker URI');
    copyBtn.innerHTML =
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';
    uriRow.append(uriText, copyBtn);
    tabBody.appendChild(uriRow);

    const status = document.createElement('p');
    status.className = 'modal-status';
    tabBody.appendChild(status);

    const connect = nip46.generateConnectUri({
      appName: 'nostr.gives',
      // Multi-relay URI for clave.casa compat. See
      // packages/shared/src/constants.ts (BUNKER_RELAYS) and
      // Vault/Operations/Clave Compat.md for why relay.powr.build is
      // included alongside our own relay.
      relays: BUNKER_RELAYS,
    });
    activeBunkerConnect = connect;
    // PM 2026-06-11 (clave.casa URI long-press fix): render a fixed
    // placeholder instead of a truncated copy of the real URI. If we
    // showed the truncated string here, a mobile user long-pressing to
    // copy would walk away with `nostrconnect:/...me=nostr.gives` and
    // paste it into clave / Alby, reproducing the "no relay specified"
    // bug we just fixed in the URI builder. The full URI is held only
    // on `activeBunkerConnect.uri` for the Copy button + QR canvas, and
    // surfaced through `title` for desktop hover tooltips.
    uriText.textContent = 'nostrconnect://...';
    uriText.title = connect.uri;
    void renderBunkerQr(connect.uri, canvas).catch((err: unknown) => {
      status.textContent = `QR render failed: ${err instanceof Error ? err.message : 'unknown'}`;
      status.dataset.kind = 'error';
    });

    copyBtn.addEventListener('click', () => {
      void navigator.clipboard
        .writeText(connect.uri)
        .then(() => {
          status.textContent = 'Copied.';
          delete status.dataset.kind;
          setTimeout(() => {
            if (status.textContent === 'Copied.') status.textContent = '';
          }, 1500);
        })
        .catch(() => {
          status.textContent = 'Copy failed. Long-press to select.';
          status.dataset.kind = 'error';
        });
    });

    const connectBtnSlot = document.createElement('div');
    connectBtnSlot.className = 'login-modal-cta';
    tabBody.appendChild(connectBtnSlot);

    // PM 2026-06-11 (clave.casa handshake fix - second iteration):
    // Start the dial-back listener as soon as the QR renders. Previously
    // we waited for the user to click "Connect", but signers like
    // clave.casa publish the kind-24133 connect-response the instant the
    // user approves on the iOS app - which is BEFORE the user can switch
    // back to the desktop browser tab to click "Connect". Ephemeral kind
    // 24133 events are not stored by relays, so the response was being
    // dropped on the floor.
    function startPairListener(currentConnect: nip46.NostrConnectUri): void {
      activePairAbort?.abort();
      const abort = new AbortController();
      activePairAbort = abort;
      status.textContent = 'Scan the QR with clave.casa, nsec.app, or Amber.';
      delete status.dataset.kind;

      void nip46
        .connectBunker({
          connect: currentConnect,
          signal: abort.signal,
        })
        .then(async (result: AuthResult) => {
          if (abort.signal.aborted || activeBunkerConnect !== currentConnect) {
            void result.signer.disconnect?.();
            return;
          }
          bunkerSignerForCleanup = result.signer;
          await completeAuth(result.pubkey, 'nip46', result.signer);
        })
        .catch((err: unknown) => {
          if (abort.signal.aborted) return;
          connectButton?.update({
            variant: 'primary',
            label: 'Generate new code',
            onClick: (): void => {
              connectButton?.destroy();
              connectButton = null;
              renderBunkerTab();
            },
          });
          status.textContent = `Bunker error: ${err instanceof Error ? err.message : 'unknown'}`;
          status.dataset.kind = 'error';
        });
    }

    connectButton = mountActionButton(connectBtnSlot, {
      variant: 'primary',
      label: 'Waiting for signer...',
      disabled: true,
      onClick: (): void => {},
    });

    startPairListener(connect);

    // iPad-on-iOS-Safari escape hatch back to the paste flow.
    if (isIosSafari()) {
      const toggle = document.createElement('a');
      toggle.className = 'modal-link';
      toggle.href = '#';
      toggle.textContent = 'Paste a bunker:// URI instead';
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        bunkerForceQrOnIos = false;
        renderBunkerTab();
      });
      tabBody.appendChild(toggle);
    }

    const link = document.createElement('a');
    link.className = 'modal-link';
    link.href = '#/help/auth';
    link.textContent = 'What is a bunker?';
    tabBody.appendChild(link);
  }

  function renderExtensionTab(): void {
    tabBody.replaceChildren();
    signInButton?.destroy();
    signInButton = null;

    const status = document.createElement('p');
    status.className = 'modal-status';

    const present = nip07.hasNip07Extension();
    if (!present) {
      const notice = document.createElement('p');
      notice.className = 'modal-body-text';
      notice.textContent =
        'No NIP-07 extension detected. Install one of the following, then reopen this dialog.';
      tabBody.appendChild(notice);

      const installList = document.createElement('div');
      installList.className = 'login-modal-extension-list';
      installList.style.display = 'flex';
      installList.style.flexDirection = 'column';
      installList.style.gap = '8px';
      installList.style.marginTop = '4px';
      tabBody.appendChild(installList);

      // Listed alphabetically. Any compliant NIP-07 extension works the
      // moment it injects window.nostr - the modal polls below.
      const options: Array<{ label: string; href: string }> = [
        { label: 'Alby', href: 'https://getalby.com/' },
        { label: 'nos2x', href: 'https://github.com/fiatjaf/nos2x' },
        { label: 'nos2x-fox (Firefox)', href: 'https://github.com/diegogurpegui/nos2x-fox' },
        { label: 'Nostore (Safari iOS)', href: 'https://apps.apple.com/app/nostore/id1666553677' },
      ];
      for (const opt of options) {
        const a = document.createElement('a');
        a.href = opt.href;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.className = 'gv-action-button';
        a.textContent = opt.label;
        a.style.textDecoration = 'none';
        a.style.display = 'inline-flex';
        a.style.justifyContent = 'center';
        a.style.width = '100%';
        installList.appendChild(a);
      }

      tabBody.appendChild(status);

      // Poll for the extension appearing. Some NIP-07 extensions inject
      // window.nostr after document_idle, so the first hasNip07Extension()
      // call can return false even when the user already has nos2x / Alby
      // installed and just opened the modal too quickly. Recheck every
      // 400ms for ~6s and re-render the tab the moment it appears.
      let attempts = 0;
      const poll = window.setInterval(() => {
        attempts += 1;
        if (nip07.hasNip07Extension()) {
          window.clearInterval(poll);
          renderExtensionTab();
        } else if (attempts >= 15) {
          window.clearInterval(poll);
        }
      }, 400);
      return;
    }

    const body = document.createElement('p');
    body.className = 'modal-body-text';
    body.textContent = 'Sign a one-time challenge so we know it is you.';
    tabBody.appendChild(body);

    const ctaSlot = document.createElement('div');
    ctaSlot.className = 'login-modal-cta';
    tabBody.appendChild(ctaSlot);
    tabBody.appendChild(status);

    let busy = false;
    signInButton = mountActionButton(ctaSlot, {
      variant: 'primary',
      label: 'Sign in',
      onClick: (): void => {
        if (busy) return;
        busy = true;
        signInButton?.update({
          variant: 'primary',
          label: 'Signing...',
          disabled: true,
          onClick: (): void => {},
        });
        status.textContent = '';
        delete status.dataset.kind;
        void nip07
          .signIn()
          .then(async (result: AuthResult) => {
            await completeAuth(result.pubkey, 'nip07', result.signer);
          })
          .catch((err: unknown) => {
            busy = false;
            signInButton?.update({
              variant: 'primary',
              label: 'Sign in',
              onClick: (): void => {
                signInButton?.destroy();
                signInButton = null;
                renderExtensionTab();
              },
            });
            if (err instanceof NoExtensionError) {
              status.textContent = 'Extension disappeared. Refresh and try again.';
            } else {
              status.textContent = `Extension error: ${err instanceof Error ? err.message : 'unknown'}`;
            }
            status.dataset.kind = 'error';
          });
      },
    });
  }

  function renderNsecTab(): void {
    tabBody.replaceChildren();
    nsecSignInButton?.destroy();
    nsecSignInButton = null;

    // Security notice (warning card).
    const notice = document.createElement('div');
    notice.className = 'nsec-notice';
    const noticeIcon = iconSvg('info', 18);
    noticeIcon.classList.add('nsec-notice-icon');
    const noticeText = document.createElement('p');
    noticeText.className = 'nsec-notice-text';
    noticeText.textContent =
      "Your nsec is encrypted at rest in this browser and never sent to our servers. A bunker or extension signer is still safer on shared or untrusted devices.";
    notice.append(noticeIcon, noticeText);
    tabBody.appendChild(notice);

    // Input row: password input + show/hide toggle.
    const inputRow = document.createElement('div');
    inputRow.className = 'nsec-input-row';
    const input = document.createElement('input');
    input.type = 'password';
    input.className = 'nsec-input mono';
    input.placeholder = 'nsec1...';
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('autocorrect', 'off');
    input.setAttribute('autocapitalize', 'off');
    input.setAttribute('spellcheck', 'false');
    input.setAttribute('aria-label', 'Nsec');

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'icon-btn nsec-toggle';
    toggle.setAttribute('aria-label', 'Show nsec');
    toggle.appendChild(iconSvg('eye', 16));
    toggle.addEventListener('click', () => {
      const reveal = input.type === 'password';
      input.type = reveal ? 'text' : 'password';
      toggle.replaceChildren(iconSvg(reveal ? 'eye-off' : 'eye', 16));
      toggle.setAttribute('aria-label', reveal ? 'Hide nsec' : 'Show nsec');
    });

    inputRow.append(input, toggle);
    tabBody.appendChild(inputRow);

    const status = document.createElement('p');
    status.className = 'modal-status';
    tabBody.appendChild(status);

    const ctaSlot = document.createElement('div');
    ctaSlot.className = 'login-modal-cta';
    tabBody.appendChild(ctaSlot);

    function submit(): void {
      const raw = input.value;
      const result = nsecMod.validateNsec(raw);
      if (!nsecMod.isNsecValidationOk(result)) {
        status.textContent = result.error;
        status.dataset.kind = 'error';
        return;
      }
      delete status.dataset.kind;
      status.textContent = '';

      const trimmed = raw.trim();
      // Persist the nsec + session record. saveNsec sets the in-memory cache
      // synchronously (so the signer wiring's loadNsec() works immediately)
      // and seals it to encrypted storage asynchronously (device-key default).
      void saveNsec(trimmed);
      saveAuth({
        pubkey: result.pubkey,
        signerType: 'nsec',
        authedAt: Date.now(),
      });
      // Best-effort scrub of the in-memory input value.
      input.value = '';
      close({ pubkey: result.pubkey });
    }

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        submit();
      }
    });

    nsecSignInButton = mountActionButton(ctaSlot, {
      variant: 'primary',
      label: 'Sign in',
      onClick: submit,
    });

    // Autofocus the input so the user can paste immediately.
    setTimeout(() => input.focus(), 0);
  }

  function renderTabBody(): void {
    // Abort any in-flight bunker pairing before swapping tabs - the URI
    // we showed is about to be replaced (if we come back to bunker) or
    // become orphaned (if we leave). Either way the listening pool needs
    // to be torn down.
    activePairAbort?.abort();
    activePairAbort = null;
    if (bunkerSignerForCleanup?.disconnect) {
      void bunkerSignerForCleanup.disconnect();
      bunkerSignerForCleanup = null;
    }
    if (activeTab === 'bunker') {
      renderBunkerTab();
    } else if (activeTab === 'extension') {
      renderExtensionTab();
    } else {
      renderNsecTab();
    }
  }

  function onSelectTab(id: string): void {
    if (!isTab(id) || id === activeTab) return;
    activeTab = id;
    tabHandle?.update({
      items: TAB_ITEMS,
      selected: activeTab,
      onSelect: onSelectTab,
    });
    renderTabBody();
  }

  tabHandle = mountSegmentedTab(tabSlot, {
    items: TAB_ITEMS,
    selected: activeTab,
    onSelect: onSelectTab,
  });

  anonButtonHandle = mountActionButton(anonSlot, {
    variant: 'secondary',
    label: 'Play without signing in',
    onClick: playAnon,
  });

  renderTabBody();

  modal = mountModalShell(document.body, {
    title: 'Sign in',
    width: 380,
    onClose: (): void => close(null),
    content,
    // PM 2026-06-10 bug H: the Login Modal is a singleton. A second
    // openLoginModal() call while one is already open is a no-op (focus
    // moves to the existing instance via modal-stack.ts).
    singletonKey: 'auth',
    // PM 2026-06-10 bug E: paint above the end-game overlay so the
    // "Sign in to share" CTA never opens behind the result modal.
    tier: 'auth',
  });

  // Singleton collision: an earlier openLoginModal() is still mounted.
  // Tear down what we built so far (we already created in-memory handles
  // for tabs / buttons) and skip the callback - the original caller's
  // close() still owns the resolution.
  if (modal.isExistingSingleton) {
    // The async tab callbacks (renderBunkerTab, renderNsecTab) assign these
    // handles after renderTabBody() runs. TS's flow analysis can't see across
    // nested-function assignments, so it narrows them to `null` here. Cast
    // back to the full union so the cleanup is a no-op when they weren't
    // assigned and a real destroy when they were.
    const tab = tabHandle as TabHandle | null;
    const connect = connectButton as ButtonHandle | null;
    const anon = anonButtonHandle as ButtonHandle | null;
    tab?.destroy();
    connect?.destroy();
    anon?.destroy();
    tabHandle = null;
    connectButton = null;
    anonButtonHandle = null;
    return;
  }
}

